"use client";

import { useState } from "react";
import { useMutation } from "convex/react";
import { CheckCircle2, ThumbsDown, ThumbsUp } from "@/components/icons";
import { api } from "@convex/_generated/api";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Textarea } from "@/components/ui/textarea";
import { ActionErrorAlert } from "@/components/ui/action-error-alert";
import { handleError } from "@/lib/handle-error";
import type { GradingFeedbackAccess } from "./grading-session";

type Rating = "helpful" | "not_helpful";

function useFeedbackSubmission(feedbackAccess: GradingFeedbackAccess) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitFeedback = useMutation(
    api.ai.grading.publicMutations.submitFeedback,
  );

  const submit = async (rating: Rating, comment?: string) => {
    setError(null);
    setSubmitting(true);
    try {
      await submitFeedback({
        ...feedbackAccess,
        rating,
        ...(comment ? { comment } : {}),
      });
      setSubmitted(true);
    } catch (caught) {
      const handled = handleError(caught, { display: "silent" });
      if (handled.action !== "ignore") {
        setError(handled.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    submitted,
    submitting,
    error,
    clearError: () => setError(null),
    submit,
  };
}

export function GradingFeedbackPanel({
  feedbackAccess,
}: {
  feedbackAccess: GradingFeedbackAccess;
}) {
  const feedback = useFeedbackSubmission(feedbackAccess);
  const [isCommenting, setIsCommenting] = useState(false);

  if (feedback.submitted) return <FeedbackThanks />;

  return (
    <div className="space-y-3 py-2">
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Was this helpful?</span>
        <IconButton
          variant="outline"
          size="compact"
          aria-label="Helpful"
          onClick={() => feedback.submit("helpful")}
          disabled={feedback.submitting}
        >
          <ThumbsUp className="h-3.5 w-3.5" />
        </IconButton>
        <IconButton
          variant="outline"
          size="compact"
          aria-label="Not helpful"
          aria-pressed={isCommenting}
          onClick={() => setIsCommenting(true)}
          disabled={feedback.submitting}
        >
          <ThumbsDown className="h-3.5 w-3.5" />
        </IconButton>
      </div>
      {isCommenting && <CommentForm feedback={feedback} />}
      <ActionErrorAlert
        from={feedback}
        reportContext={{ titlePrefix: "Grading feedback failed" }}
      />
    </div>
  );
}

function FeedbackThanks() {
  return (
    <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
      <CheckCircle2 className="h-4 w-4 shrink-0 text-success-strong" />
      Thanks for your feedback!
    </div>
  );
}

function CommentForm({
  feedback,
}: {
  feedback: ReturnType<typeof useFeedbackSubmission>;
}) {
  const [comment, setComment] = useState("");
  return (
    <div className="space-y-2">
      <Textarea
        aria-label="What could be improved?"
        placeholder="What could be improved?"
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        rows={3}
        className="resize-none"
        disabled={feedback.submitting}
      />
      <Button
        variant="primary"
        size="sm"
        onClick={() =>
          feedback.submit("not_helpful", comment.trim() || undefined)
        }
        disabled={feedback.submitting}
      >
        Submit
      </Button>
    </div>
  );
}
